import { Button } from '@mantine/core';
import { useModals } from '@mantine/modals';
import classnames from 'classnames';
import { DataTable } from 'mantine-datatable';
import { nanoid } from 'nanoid';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useFetchExpenseBalance from 'src/Hooks/Queries/useFetchExpenseBalance';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import useNavigateKeepingSearch from 'src/Hooks/Shared/useNavigateKeepingSearch';
import { SettlementTargetDtoTypeEnum } from 'src/Services/API/generated';
import { formatEuros } from 'src/Utils/formatEuros';
import { formatSettlementTargetName } from 'src/Utils/formatSettlementTargetName';

import styles from './ExpenseBalanceList.module.scss';

const ExpenseBalanceList = () => {
  const { data, isLoading } = useFetchExpenseBalance();
  const { t } = useTranslation();
  const modals = useModals();
  const assetId = useCurrentAssetId();
  const navigate = useNavigateKeepingSearch();

  const owedData = useMemo(
    () => [
      ...(data?.iOwe ?? []).map(o => ({ ...o, iOwe: true, id: nanoid() })),
      ...(data?.owedToMe ?? []).map(o => ({ ...o, iOwe: false, id: nanoid() })),
    ],
    [data?.iOwe, data?.owedToMe]
  );

  const handleOpenDetails = (item: (typeof owedData)[0]) => {
    const target = item.iOwe ? item.to : item.from;
    const targetName = formatSettlementTargetName(target);

    modals.openContextModal('balanceDetails', {
      title: t('expenses.balanceDetails.title', 'Détails du solde avec {{name}}', {
        name: targetName,
      }),
      innerProps: {
        assetId: assetId!,
        userId: target.type === SettlementTargetDtoTypeEnum.User ? target.id : undefined,
        bankAccountId:
          target.type === SettlementTargetDtoTypeEnum.BankAccount ? target.id : undefined,
        contactId: target.type === SettlementTargetDtoTypeEnum.Contact ? target.id : undefined,
        targetName,
      },
      size: 'lg',
    });
  };

  return (
    <div className={styles.container}>
      {owedData.length > 0 && (
        <DataTable
          withRowBorders={false}
          style={{ width: '100%', minHeight: '10rem' }}
          records={owedData}
          rowStyle={() => ({ height: 58 })}
          noRecordsText={t('expenses.empty', 'Aucune opération')}
          fetching={isLoading}
          columns={[
            {
              accessor: 'from',
              title: '',
              render: ({ from, to, iOwe }) => (
                <div className={styles.userName}>
                  {formatSettlementTargetName(iOwe ? to : from)}
                </div>
              ),
            },
            {
              accessor: 'amount',
              title: '',
              render: ({ amount, iOwe }) => {
                return (
                  <div
                    className={classnames(
                      styles.amount,
                      { [styles.amountIOwe]: iOwe },
                      { [styles.amountOwedToMe]: !iOwe }
                    )}
                  >
                    {`${iOwe ? '-' : '+'} ${formatEuros(Math.abs(amount))}`}
                  </div>
                );
              },
            },
            {
              accessor: 'details',
              title: '',
              render: item => {
                return (
                  <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                    {item.iOwe ? (
                      <Button
                        variant="filled"
                        size="xs"
                        onClick={e => {
                          e.stopPropagation();
                          const from = item.from;
                          const to = item.to;
                          navigate('new-transfer', {
                            state: {
                              amount: item.amount,
                              fromUserId:
                                from.type === SettlementTargetDtoTypeEnum.User
                                  ? from.id
                                  : undefined,
                              fromBankAccountId:
                                from.type === SettlementTargetDtoTypeEnum.BankAccount
                                  ? from.id
                                  : undefined,
                              toUserId:
                                to.type === SettlementTargetDtoTypeEnum.User ? to.id : undefined,
                              toBankAccountId:
                                to.type === SettlementTargetDtoTypeEnum.BankAccount
                                  ? to.id
                                  : undefined,
                            },
                          });
                        }}
                      >
                        {t('expenses.balanceDetails.refund', 'Rembourser')}
                      </Button>
                    ) : (
                      <Button
                        variant="filled"
                        size="xs"
                        onClick={e => {
                          e.stopPropagation();
                          const from = item.from;
                          const to = item.to;
                          navigate('new-transfer', {
                            state: {
                              amount: item.amount,
                              fromUserId:
                                from.type === SettlementTargetDtoTypeEnum.User
                                  ? from.id
                                  : undefined,
                              fromBankAccountId:
                                from.type === SettlementTargetDtoTypeEnum.BankAccount
                                  ? from.id
                                  : undefined,
                              toUserId:
                                to.type === SettlementTargetDtoTypeEnum.User ? to.id : undefined,
                              toBankAccountId:
                                to.type === SettlementTargetDtoTypeEnum.BankAccount
                                  ? to.id
                                  : undefined,
                            },
                          });
                        }}
                      >
                        {t('expenses.balanceDetails.recordRefund', 'Enregistrer le remboursement')}
                      </Button>
                    )}
                    <Button
                      variant="light"
                      size="xs"
                      onClick={e => {
                        e.stopPropagation();
                        handleOpenDetails(item);
                      }}
                    >
                      {t('expenses.balanceDetails.button', 'Détails')}
                    </Button>
                  </div>
                );
              },
            },
          ]}
        />
      )}
    </div>
  );
};

export default ExpenseBalanceList;
